<i18n>
[
    "product__allSoldOut--message",
    "product__skuSoldOut--message",
]
</i18n>

<template>
    <div class="o-row o-row-full o-row--nested">
        <div class="c-sku-selector o-extra-small--12">
            <BaseRadioButtonGroup
                v-if="sizeTypes.length > 1"
                :id="`${skuSelectorId}-size-type`"
                v-model="selectedSizeType"
                :name="`${skuSelectorName}-size-type`"
                :isRequired="true"
                :radioButtonItems="sizeTypes"
                buttonGroupClass="c-sku-selector__size-type u-spacer--0pt5"
                :checkedItem="selectedSizeType"
                :styleType="BUTTON_STYLE_TYPES.TAB"
                :isLight="true"
                @update:model-value="updateSizeType"
            />
            <BaseRadioButtonGroup
                v-if="sizes.length"
                :id="`${skuSelectorId}-size`"
                v-model="selectedSize"
                :name="`${skuSelectorName}-size`"
                :isRequired="true"
                :radioButtonItems="sizes"
                buttonGroupClass="c-sku-selector__size u-spacer--1"
                :checkedItem="selectedSize"
                :styleType="BUTTON_STYLE_TYPES.INLINE_TILE"
                :isLight="true"
                @click="clickEvent"
                @update:model-value="updateSize"
            />
            <BaseSupportingTextError
                :errorText="skuErrorMessage || errorMessage"
                class="u-spacer--2"
            />
            <ProductFitSliderScale
                v-if="productDetails"
                class="u-spacer--3"
                :reviewSummary="productDetails.reviewSummary"
                :smallText="true"
            />
        </div>
    </div>
</template>

<script>
import { get } from 'lodash-es';

import { BUTTON_STYLE_TYPES } from '~coreModules/core/js/constants';
import { PRODUCT_SIZE_TYPES } from '~modules/core/js/constants';

import BaseRadioButtonGroup from '~coreModules/core/components/ui/form-elements/radios/BaseRadioButtonGroup.vue';
import BaseSupportingTextError from '~coreModules/core/components/ui/form-elements/BaseSupportingTextError.vue';

import ProductFitSliderScale from '~routes/product/components/ProductFitSliderScale.vue';

export default {
    name: 'SkuSelector',
    components: {
        BaseRadioButtonGroup,
        BaseSupportingTextError,
        ProductFitSliderScale,
    },
    props: {
        skuSelectorId: {
            type: String,
            required: true,
        },
        skuSelectorName: {
            type: String,
            required: true,
        },
        selectedColorCode: {
            type: String,
            required: true,
        },
        sizeGroups: {
            type: Array,
            required: true,
        },
        initialSizeType: {
            type: String,
            default: '',
        },
        initialSize: {
            type: String,
            default: '',
        },
        initialSku: {
            type: String,
            default: '',
        },
        skuErrorMessage: {
            type: String,
            default: '',
        },
        productDetails: {
            type: Object,
            default: null,
        },
        smallSkuSelector: {
            type: Boolean,
            default: false,
        },
        emitClickEvent: {
            type: Boolean,
            default: false,
        },
        purchasableSkus: {
            type: Array,
            default: () => [],
        },
        hasError: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['click', 'updateSku'],
    data() {
        return {
            BUTTON_STYLE_TYPES,
            selectedSku: this.initialSku,
            selectedSizeType: this.initialSizeType,
            selectedSize: this.initialSize,
            sizeTypes: [],
            sizes: [],
            errorMessage: '',
        };
    },
    computed: {
        noInventory() {
            const hasInventory = this.sizeGroups
                .find(size => size.includedSkus.find(sku => sku.availableInventory > 0));
            return !hasInventory;
        },
    },
    watch: {
        selectedColorCode(newColor, oldColor) {
            if (newColor !== oldColor) {
                this.setSkuSelector();
            }
        },
        initialSize(newSize) {
            this.selectedSize = newSize;
            this.setSku();
        },
        initialSizeType(newSizeType) {
            this.selectedSizeType = newSizeType;
        },
        selectedSizeType() {
            this.setSizes();
        },
    },
    created() {
        this.setSkuSelector();
    },
    methods: {
        setSkuSelector() {
            this.setSizeTypes();
            this.setSizes();
            this.setSku();
        },
        setSizeTypes() {
            if (this.sizeGroups.length === 1 && this.sizeGroups[0].groupCode === PRODUCT_SIZE_TYPES.standard) {
                this.selectedSizeType = PRODUCT_SIZE_TYPES.standard;
                this.sizeTypes = [];
                return;
            }

            this.sizeTypes = this.sizeGroups.map(sizeGroup => ({
                label: sizeGroup.displayName,
                value: sizeGroup.groupCode,
            }));

            // TODO: error if there are no sizeTypes
            // https://urbnit.atlassian.net/browse/TYP-3046
            if (!this.selectedSizeType) {
                this.selectedSizeType = this.sizeTypes[0].value;
            }
        },
        setSizes() {
            if (!this.selectedSizeType) {
                this.selectedSizeType = this.sizeGroups?.[0].groupCode;
            }

            this.sizes = this.sizeGroups
                .find(sizeGroup => sizeGroup.groupCode === this.selectedSizeType)
                .includedSkus
                .map(sku => ({
                    label: sku.size.displayName,
                    value: sku.size.code,
                    disabled: sku.availableInventory <= 0 && !this.purchasableSkus.includes(sku.skuId),
                }));

            // TODO: error if there are no sizes
            // https://urbnit.atlassian.net/browse/TYP-3046
        },
        updateSizeType(sizeType) {
            this.selectedSizeType = sizeType;
            this.setSizes();
            this.updateSize('');
        },
        updateSize(size) {
            this.selectedSize = size;
            this.setSku(true);
        },
        setSku(emitEvent = false) {
            const sizeGrouping = this.sizeGroups.find(sizeGroup => sizeGroup.groupCode === this.selectedSizeType);
            const sku = sizeGrouping.includedSkus.find(
                includedSku => (
                    includedSku.size.code === this.selectedSize &&
                    includedSku.availableInventory > 0
                ));

            this.selectedSku = get(sku, 'skuId', '');

            if (emitEvent) {
                this.$emit('updateSku', sku);
            }
        },
        clickEvent(event) {
            if (this.emitClickEvent) {
                this.$emit('click', event);
            } else {
                this.sizeClick(event);
            }
        },
        sizeClick(event) {
            if (event.disabled && this.noInventory) {
                this.errorMessage = this.$t('product__allSoldOut--message');
            } else if (event.disabled && !this.noInventory) {
                this.errorMessage = this.$t('product__skuSoldOut--message', [event.label]);
            } else {
                this.errorMessage = '';
            }
        },
    },
};
</script>

<style lang="scss">

</style>
